import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import clsx from "clsx";
import Select from "react-select";
import { BASE_URL } from "../../config";

// import { HelpCircle } from "lucide-react"
// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { HelpCircle } from "lucide-react"
// import { TooltipProvider, Tooltip } from "@/components/ui/Tooltip"
import { TooltipProvider, Tooltip } from "../../components/ui/Tooltip"
// import PlanTypeInfoTooltip from "../PlanTypeInfoTooltip_v2";
import PlanTypeInfoTooltip_v1 from "../PlanTypeInfoTooltip_v1";


const formatDate = (d) => {
  if (!d) return "";
  const m = require("dayjs")(d);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const AddPlanForm = () => {
  const { corpCode } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plan_coverage");
  const [showBenefitLimitSpecs, setShowBenefitLimitSpecs] = useState(false);
  const [formData, setFormData] = useState({
    corp_code: corpCode,
    corp_name: "",
    plan_code: "",
    plan_name: "",
    effective_date: "",
    expiry_date: "",
    shared: false,
    plan_type: "",
    pec_dread: "",
    preexist: "",
    plan_limit_type: "",
    intervening_prd: "",
    copay_corp: "",
    copay_member: "",
    exclusion1: "",
    no_of_visits: "",
    update_by: "system_user", // Replace with actual user
    anes_basic_rate: "",
    anes_major_rate: "",
    shared_dependent: false,
    ataccess_num: "",
    mpsyche_num: "",
    ftw: false,
    coverage_codes: [],
    ben_basic: [],
    rnb_amt: "",
    rnb_type: "",
  });

  const coverageHierarchy = {
    OUTPATIENT: [
      "OUTPATIENT-BASIC-CONSULTATION",
      "OUTPATIENT-REFERRAL-TO-SPECIALIST",
      "OUTPATIENT-LABORATORY",
      "OUTPATIENT-OPD-OR",
      "OUTPATIENT-FACILITY-FEE",
      "OUTPATIENT-CLINIC-SETTING",
      "ATACCESS",
      "MEDPADALA",
      "MPSYCHE",
    ],
    MATERNITY: [
      "MATERNITY-BASIC-CONSULTATION",
      "MATERNITY-LABORATORY",
      "MATERNITY-ADMISSION",
      "MATERNITY-DISCHARGE",
    ],
    REIMBURSEMENT: ["RI-INPATIENT", "RI-OUTPATIENT"],
  };

  const coverageMap = {
    inpatient: ["INPATIENT-ADMISSION", "INPATIENT-DISCHARGE"],
    outpatient: ["OUTPATIENT"],
    basic_consultation: ["OUTPATIENT-BASIC-CONSULTATION"],
    referral_to_specialist: ["OUTPATIENT-REFERRAL-TO-SPECIALIST"],
    op_laboratory: ["OUTPATIENT-LABORATORY"],
    opdor: ["OUTPATIENT-OPD-OR"],
    facility_fee: ["OUTPATIENT-FACILITY-FEE"],
    clinic_setting: ["OUTPATIENT-CLINIC-SETTING"],
    ataccess: ["ATACCESS"],
    medpadala: ["MEDPADALA"],
    mpsyche: ["MPSYCHE"],
    annual_physical_exam: ["APE"],
    emergency: ["EMERGENCY"],
    maternity: ["MATERNITY"],
    pre_and_post_natal_consultation: ["MATERNITY-BASIC-CONSULTATION"],
    laboratory: ["MATERNITY-LABORATORY"],
    delivery: ["MATERNITY-ADMISSION", "MATERNITY-DISCHARGE"],
    reimbursement: ["REIMBURSEMENT"],
    ri_inpatient: ["RI-INPATIENT"],
    ri_outpatient: ["RI-OUTPATIENT"],
  };

  const benefitMap = {
    ben_lmt_ip_max_limit: { coverage: "INPATIENT", code: "ip_max_limit_basic" },
    ben_lmt_ip_room_n_board: { coverage: "INPATIENT", code: "LMKIUWSQ64" },
    ben_lmt_ip_misc_hospital_services: { coverage: "INPATIENT", code: "VLJETCUQOC" },
    ben_lmt_ip_surgical_fee: { coverage: "INPATIENT", code: "WQTJXUBHZR" },
    ben_lmt_ip_physician_fee: { coverage: "INPATIENT", code: "BXAENWLC89" },
    ben_lmt_ip_opdor: { coverage: "INPATIENT", code: "KEKMDSWADP" },
    ben_lmt_ip_ecu: { coverage: "INPATIENT", code: "TB71MTN9K0" },
    ben_lmt_ip_surgical_fee_anes_fee: { coverage: "INPATIENT", code: "WXLZFNRUBH" },

    ben_lmt_op_max_limit: { coverage: "OUTPATIENT", code: "op_max_limit_basic" },
    ben_lmt_op_basic_consultation: { coverage: "OUTPATIENT", code: "MKMG7IA5AG" },
    ben_lmt_op_laboratory: { coverage: "OUTPATIENT", code: "EGQIHVQB58" },
    ben_lmt_op_clinic_setting: { coverage: "OUTPATIENT", code: "HYTJ8K1JTI" },
    ben_lmt_op_emergency: { coverage: "OUTPATIENT", code: "CFDEASKQ19" },
    ben_lmt_op_medicine: { coverage: "OUTPATIENT", code: "QAFRMMWNEL" },
    ben_lmt_op_ataccess: { coverage: "ATACCESS", code: "YL440L5KBY" },
    ben_lmt_op_medpadala: { coverage: "MEDPADALA", code: "WTIGQLQA25" },
    ben_lmt_op_mpsyche: { coverage: "MPSYCHE", code: "MPSYCHE" },

    ben_lmt_mat_max_limit: { coverage: "MATERNITY", code: "mat_max_limit_basic" },
    ben_lmt_mat_normal_delivery: { coverage: "MATERNITY", code: "K20I8CQQ9T" },
    ben_lmt_mat_cs_delivery: { coverage: "MATERNITY", code: "AAM69QNWLP" },
    ben_lmt_mat_pre_natal: { coverage: "MATERNITY", code: "QFT8DSWXOL" },
    ben_lmt_mat_post_natal: { coverage: "MATERNITY", code: "MWS9XG8X04" },
    
    ben_lmt_ape_max_limit: { coverage: "APE", code: "ape_max_limit_basic" },
  };

  const suggestionOptions = [
    "as charged",
    "unlimited",
    "not covered",
    "covered",
    "shared",
    "MBL",
    "Subject to MBL",
    "Subject to MBL and Inner Limits",
    "Covered except prescribed medicines",
    "Take Home Medicines not covered",
  ];

  const totalVisitSuggestionOptions = ["unlimited"];
  const countLimitFields = ["ataccess_num", "mpsyche_num"];
  const rateLimitFields = ["anes_basic_rate", "anes_major_rate"];
  const totalVisitField = "ben_lmt_op_total_no_visit_basic";
  const nonBenefitLimitFields = [...countLimitFields, ...rateLimitFields];

  const isRoomAndBoardCategoryField = (name) =>
    name === "ben_lmt_ip_room_n_board_cat" || name.startsWith("ben_lmt_ip_room_n_board_cat_");

  const isTotalVisitField = (name) => name === totalVisitField || name === "no_of_visits";

  const isBenefitLimitField = (name) =>
    (name.startsWith("ben_lmt_") && !isRoomAndBoardCategoryField(name)) ||
    nonBenefitLimitFields.includes(name);

  const isRateField = (name) =>
    name === "anes_basic_rate" ||
    name === "anes_major_rate" ||
    name.startsWith("ben_lmt_ip_surgical_fee_anes_fee");

  const getSuggestionOptionsForField = (name) =>
    isTotalVisitField(name) ? totalVisitSuggestionOptions : suggestionOptions;

  const getSuggestionListId = (name) =>
    isTotalVisitField(name) ? "totalVisitSuggestions" : "benefitLimitSuggestions";

  const getComparableNumericValue = (name, value) => {
    const trimmedValue = String(value ?? "").trim();
    if (isRateField(name) && trimmedValue.endsWith("%")) {
      return trimmedValue.slice(0, -1).trim();
    }
    return trimmedValue;
  };

  const isNonNegativeNumber = (name, value) => {
    const numericValue = getComparableNumericValue(name, value);
    return /^(\d+(\.\d*)?|\.\d+)$/.test(numericValue);
  };

  const getCanonicalOption = (name, value) => {
    const trimmedValue = String(value ?? "").trim();
    return getSuggestionOptionsForField(name).find(
      (option) => option.toLowerCase() === trimmedValue.toLowerCase()
    );
  };

  const getLimitValidationMessage = (name, value) => {
    if (!isBenefitLimitField(name)) return "";
    const trimmedValue = String(value ?? "").trim();
    if (!trimmedValue) return "";

    const numericValue = getComparableNumericValue(name, trimmedValue);
    if (numericValue.startsWith("-")) {
      return isTotalVisitField(name)
        ? "Total Number of Visit cannot be negative."
        : "Benefit/count limits cannot be negative.";
    }

    if (isNonNegativeNumber(name, trimmedValue) || getCanonicalOption(name, trimmedValue)) {
      return "";
    }

    return isTotalVisitField(name)
      ? "Total Number of Visit only accepts unlimited or a non-negative number."
      : "Benefit/count limits only accept the allowed options or a non-negative number.";
  };

  const isPotentialLimitValue = (name, value) => {
    if (!isBenefitLimitField(name)) return true;
    const trimmedValue = String(value ?? "").trim();
    if (!trimmedValue) return true;

    const numericValue = getComparableNumericValue(name, trimmedValue);
    if (numericValue.startsWith("-")) return false;
    if (/^(\d+(\.\d*)?|\.\d*)$/.test(numericValue)) return true;

    const lowerValue = trimmedValue.toLowerCase();
    return getSuggestionOptionsForField(name).some((option) =>
      option.toLowerCase().startsWith(lowerValue)
    );
  };

  const normalizeLimitFields = (data) => {
    const normalized = { ...data };
    Object.keys(normalized).forEach((name) => {
      const canonicalOption = getCanonicalOption(name, normalized[name]);
      if (isBenefitLimitField(name) && canonicalOption) {
        normalized[name] = canonicalOption;
      }
    });
    normalized.no_of_visits = normalized[totalVisitField] || normalized.no_of_visits || "";
    return normalized;
  };

  const validateLimitFields = (data) => {
    const normalizedData = normalizeLimitFields(data);
    const invalidField = Object.keys(normalizedData).find((name) =>
      Boolean(getLimitValidationMessage(name, normalizedData[name]))
    );

    if (invalidField) {
      toast.error(getLimitValidationMessage(invalidField, normalizedData[invalidField]), {
        toastId: `${invalidField}-validation`,
      });
      return null;
    }

    return normalizedData;
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  useEffect(() => {
    const fetchCorpNameAndDraft = async () => {
      setIsLoading(true);
      try {
        const corpResponse = await axios.get(`${BASE_URL}/corporates/${corpCode}`, { headers: getAuthHeaders() });
        const corporate = corpResponse.data.data || {};
        const corpName = corporate.corp_name;
        const draftResponse = await axios.post(`${BASE_URL}/plan-coverages/${corpCode}/draft`, {}, { headers: getAuthHeaders() });
        const draftData = draftResponse.data.data || {};
        setFormData((prev) => ({
          ...prev,
          ...draftData,
          coverage_codes: [],
          ben_basic: [],
          corp_name: corpName,
          effective_date:
            draftData.effective_date || formatDate(corporate.effective_date),
          expiry_date:
            draftData.expiry_date || formatDate(corporate.expiry_date),
        }));
      } catch (error) {
        toast.error("Failed to initialize form: " + (error.response?.data?.message || error.message));
        setTimeout(() => navigate("/plan-and-coverage-benefits-setup"), 2000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCorpNameAndDraft();
  }, [corpCode, navigate]);

  const handleInputChange = (value, name) => {
    if (isBenefitLimitField(name) && !isPotentialLimitValue(name, value)) {
      toast.error(getLimitValidationMessage(name, value) || "Only allowed options or non-negative numbers are accepted.", {
        toastId: `${name}-input-validation`,
      });
      return;
    }

    if (name.startsWith("cov_")) {
      setFormData((prev) => {
        const coverage_codes = [...(prev.coverage_codes || [])];
        const mainCoverage = name.replace("cov_", "");
        const codesToToggle = coverageMap[mainCoverage] || [];
        if (value) {
          coverage_codes.push(...codesToToggle);
          if (coverageHierarchy[mainCoverage.toUpperCase()]) {
            coverage_codes.push(...coverageHierarchy[mainCoverage.toUpperCase()]);
          }
        } else {
          codesToToggle.forEach((code) => {
            const index = coverage_codes.indexOf(code);
            if (index !== -1) coverage_codes.splice(index, 1);
          });
          if (coverageHierarchy[mainCoverage.toUpperCase()]) {
            coverageHierarchy[mainCoverage.toUpperCase()].forEach((code) => {
              const index = coverage_codes.indexOf(code);
              if (index !== -1) coverage_codes.splice(index, 1);
            });
          }
        }
        const mainCoverageKey = Object.keys(coverageHierarchy).find((key) =>
          coverageHierarchy[key].includes(codesToToggle[0])
        );
        if (mainCoverageKey && !value) {
          const allSubItemsChecked = coverageHierarchy[mainCoverageKey].every((code) =>
            coverage_codes.includes(code)
          );
          if (!allSubItemsChecked) {
            const mainCode = coverageMap[mainCoverageKey.toLowerCase()][0];
            const index = coverage_codes.indexOf(mainCode);
            if (index !== -1) coverage_codes.splice(index, 1);
          }
        } else if (mainCoverageKey && value) {
          const allSubItemsChecked = coverageHierarchy[mainCoverageKey].every((code) =>
            coverage_codes.includes(code)
          );
          if (allSubItemsChecked && !coverage_codes.includes(coverageMap[mainCoverageKey.toLowerCase()][0])) {
            coverage_codes.push(coverageMap[mainCoverageKey.toLowerCase()][0]);
          }
        }
        return { ...prev, coverage_codes: [...new Set(coverage_codes)] };
      });
    } else if (name === "copay_corp") {
      setFormData((prev) => {
        let copayCorp = parseFloat(value) || 0;
        if (copayCorp > 100) copayCorp = 100;
        const copayMember = 100 - copayCorp;
        return { ...prev, copay_corp: copayCorp.toString(), copay_member: copayMember.toString() };
      });
    } else if (name.startsWith("ben_lmt_") || name === "ataccess_num" || name === "mpsyche_num" || name === "anes_basic_rate" || name === "anes_major_rate") {
      setFormData((prev) => {
        const ben_basic = [...(prev.ben_basic || [])];
        const isBenefitField = name.startsWith("ben_lmt_");
        const field = isBenefitField ? name.split("_").slice(0, -1).join("_") : name;
        const type = isBenefitField ? name.split("_").pop() : null;
        const benefit = benefitMap[field];
        let finalValue = value;

        if (isTotalVisitField(name)) {
          return { ...prev, [name]: finalValue, no_of_visits: finalValue, ben_basic: [...ben_basic] };
        }

        if (field === "ben_lmt_ip_room_n_board_cat" && value && value !== "NA") {
          return {
            ...prev,
            ben_lmt_ip_room_n_board_basic: "0",
            ben_lmt_ip_room_n_board_major: "0",
            rnb_type: value,
            [name]: value,
          };
        } else if (field === "ben_lmt_ip_room_n_board" && (type === "basic" || type === "major")) {
          return {
            ...prev,
            ben_lmt_ip_room_n_board_cat: "NA",
            ben_lmt_ip_room_n_board_cat_basic: type === "basic" ? value : prev.ben_lmt_ip_room_n_board_cat_basic,
            ben_lmt_ip_room_n_board_cat_major: type === "major" ? value : prev.ben_lmt_ip_room_n_board_cat_major,
            rnb_amt: "0",
            rnb_type: "NA",
            [name]: finalValue,
            ben_basic: (() => {
              const existingIndex = ben_basic.findIndex((b) => b.startsWith(`${benefit.coverage}|${benefit.code}`));
              if (existingIndex !== -1) {
                const parts = ben_basic[existingIndex].split("|");
                parts[type === "basic" ? 2 : 3] = finalValue || "0";
                ben_basic[existingIndex] = parts.join("|");
              } else {
                ben_basic.push(`${benefit.coverage}|${benefit.code}|${type === "basic" ? finalValue || "0" : "0"}|${type === "major" ? finalValue || "0" : "0"}`);
              }
              return [...ben_basic];
            })(),
          };
        }
        if (field === "ben_lmt_ip_surgical_fee" || name === "anes_basic_rate" || name === "anes_major_rate") {
          const isSurgicalFee = field === "ben_lmt_ip_surgical_fee";
          const isBasic = type === "basic" || name === "anes_basic_rate";
          const surgicalFeeField = isBasic ? "ben_lmt_ip_surgical_fee_basic" : "ben_lmt_ip_surgical_fee_major";
          const anesRateField = isBasic ? "anes_basic_rate" : "anes_major_rate";
          const anesFeeField = isBasic ? "ben_lmt_ip_surgical_fee_anes_fee_basic" : "ben_lmt_ip_surgical_fee_anes_fee_major";
          let surgicalFee = isSurgicalFee ? finalValue : prev[surgicalFeeField];
          let anesRate = isSurgicalFee ? prev[anesRateField] : value;
          let anesFee;
          if (anesRate && !isNaN(anesRate.replace("%", "")) && surgicalFee && !isNaN(surgicalFee)) {
            const rate = parseFloat(anesRate.replace("%", "")) / 100;
            anesFee = (parseFloat(surgicalFee) * rate).toFixed(2);
          } else {
            anesFee = finalValue;
          }
          if (benefit) {
            const existingIndex = ben_basic.findIndex((b) => b.startsWith(`${benefit.coverage}|${benefit.code}`));
            if (existingIndex !== -1) {
              const parts = ben_basic[existingIndex].split("|");
              parts[type === "basic" ? 2 : 3] = finalValue || "0";
              ben_basic[existingIndex] = parts.join("|");
            } else {
              ben_basic.push(`${benefit.coverage}|${benefit.code}|${type === "basic" ? finalValue || "0" : "0"}|${type === "major" ? finalValue || "0" : "0"}`);
            }
          }
          return {
            ...prev,
            [name]: finalValue,
            [anesFeeField]: anesFee,
            ben_basic: [...ben_basic],
          };
        } else if (["ataccess_num", "mpsyche_num", "anes_basic_rate", "anes_major_rate"].includes(name)) {
          let updatedValue = value;
          if ((name === "anes_basic_rate" || name === "anes_major_rate") && value && !isNaN(value)) {
            updatedValue = `${value}%`;
          }
          return { ...prev, [name]: updatedValue };
        }
        return { ...prev, [name]: finalValue, ben_basic: [...ben_basic] };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFocus = (name) => {
    if (name === "anes_basic_rate" || name === "anes_major_rate") {
      setFormData((prev) => {
        const value = prev[name];
        if (value && !suggestionOptions.includes(value)) {
          return { ...prev, [name]: value.replace("%", "") };
        }
        return prev;
      });
    }
  };

  const handleBlur = (name) => {
    if (isBenefitLimitField(name)) {
      const validationMessage = getLimitValidationMessage(name, formData[name]);
      if (validationMessage) {
        toast.error(validationMessage, {
          toastId: `${name}-blur-validation`,
        });
        return;
      }

      const canonicalValue = getCanonicalOption(name, formData[name]);
      if (canonicalValue && canonicalValue !== formData[name]) {
        setFormData((prev) => ({
          ...prev,
          [name]: canonicalValue,
          ...(isTotalVisitField(name) ? { no_of_visits: canonicalValue } : {}),
        }));
      }
    }

    if (name === "anes_basic_rate" || name === "anes_major_rate") {
      setFormData((prev) => {
        const value = prev[name];
        if (value && !isNaN(value) && !suggestionOptions.includes(value)) {
          const anesFeeField = name === "anes_basic_rate" ? "ben_lmt_ip_surgical_fee_anes_fee_basic" : "ben_lmt_ip_surgical_fee_anes_fee_major";
          const surgicalFeeField = name === "anes_basic_rate" ? "ben_lmt_ip_surgical_fee_basic" : "ben_lmt_ip_surgical_fee_major";
          const surgicalFee = prev[surgicalFeeField];
          let anesFee = value;
          if (surgicalFee && !isNaN(surgicalFee) && !suggestionOptions.includes(surgicalFee)) {
            const rate = parseFloat(value) / 100;
            anesFee = (parseFloat(surgicalFee) * rate).toFixed(2);
          }
          return {
            ...prev,
            [name]: `${value}%`,
            [anesFeeField]: anesFee,
          };
        } else {
          const anesFeeField = name === "anes_basic_rate" ? "ben_lmt_ip_surgical_fee_anes_fee_basic" : "ben_lmt_ip_surgical_fee_anes_fee_major";
          return {
            ...prev,
            [name]: value,
            [anesFeeField]: value,
          };
        }
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const normalizedFormData = validateLimitFields(formData);
    if (!normalizedFormData) return;

    setFormData(normalizedFormData);
    setIsLoading(true);
    try {
      const payload = {
        ...normalizedFormData,
        coverage_codes: normalizedFormData.coverage_codes.join(","),
        ben_basic: normalizedFormData.ben_basic.join(","),
        rnb_amt: normalizedFormData.ben_lmt_ip_room_n_board_basic || "0",
        rnb_type: normalizedFormData.ben_lmt_ip_room_n_board_cat || "NA",
      };
      await axios.put(`${BASE_URL}/plan-coverages/${formData.plan_code}`, payload, { headers: getAuthHeaders() });
      toast.success("Plan saved successfully");
      setTimeout(() => {
        navigate("/plan-and-coverage-benefits-setup");
      }, 800);
    } catch (error) {
      if (error.response?.data?.message) {
        if (typeof error.response.data.message === "object") {
          Object.values(error.response.data.message).forEach((errArray) => {
            errArray.forEach((err) => toast.error(err));
          });
        } else {
          toast.error(error.response.data.message);
        }
      } else {
        toast.error("Failed to save plan: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { key: "plan_coverage", label: "Plan Coverage", icon: ShieldCheckIcon },
    { key: "benefit_limits", label: "Benefit Limits", icon: CurrencyDollarIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <ShieldCheckIcon className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">Add New Plan</h1>
              <p className="text-sm">{formData.corp_name || "Loading..."} (Code: {formData.plan_code || "Generating..."})</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600 mr-2" />
            <span className="text-gray-600">Loading...</span>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white shadow-xl rounded-lg overflow-visible"
          >
            <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur">
              <div className="flex gap-1 px-4 py-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={clsx(
                      "relative inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      activeTab === tab.key ? "text-blue-700" : "text-gray-600 hover:text-gray-800"
                    )}
                  >
                    <tab.icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                    {activeTab === tab.key && (
                      <span className="absolute inset-x-2 -bottom-1 h-0.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={handleSave} className="p-6 pb-0">
              {activeTab === "plan_coverage" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Corporate Name</label>
                      <input
                        type="text"
                        value={formData.corp_name || ""}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plan Code</label>
                      <input
                        type="text"
                        value={formData.plan_code || ""}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Start Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={formatDate(formData.effective_date)}
                        onChange={(e) => handleInputChange(e.target.value, "effective_date")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">End Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={formatDate(formData.expiry_date)}
                        onChange={(e) => handleInputChange(e.target.value, "expiry_date")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.shared}
                          onChange={(e) => handleInputChange(e.target.checked, "shared")}
                          className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">Shared Limits</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plan Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={formData.plan_name || ""}
                        onChange={(e) => handleInputChange(e.target.value, "plan_name")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter plan name"
                      />
                    </div>
                    <div>
                      {/* <label className="block text-sm font-medium text-gray-700">
                        Plan Type <span className="text-red-500">*</span>
                      </label> */}
                      <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                        Plan Type <span className="text-red-500">*</span>

                        {/* <TooltipProvider>
                          <Tooltip
                            content={
                              <div className="max-w-xs text-sm">
                                <p>
                                  When enrolling a new member, the plan availability depends on their member type:
                                </p>
                                <ul className="list-disc pl-4 mt-1 space-y-1">
                                  <li><strong>Principal:</strong> This plan is available for new members with Principal type.</li>
                                  <li><strong>Dependent:</strong> This plan is available only for new members with Dependent type.</li>
                                  <li><strong>Both:</strong> This plan can be used by new members whether Principal or Dependent.</li>
                                </ul>
                              </div>
                            }
                          >
                            <HelpCircle className="h-4 w-4 text-gray-500 cursor-pointer" />
                          </Tooltip>
                        </TooltipProvider> */}
                        {/* <PlanTypeInfoTooltip></PlanTypeInfoTooltip> */}
                        <PlanTypeInfoTooltip_v1></PlanTypeInfoTooltip_v1>

                      </label>
                      <Select
                        options={[
                          { value: "", label: "Select" },
                          { value: "P", label: "P - Principal" },
                          { value: "D", label: "D - Dependent" },
                          { value: "B", label: "B - Both" },
                        ]}
                        value={{ value: formData.plan_type, label: formData.plan_type ? { P: "P - Principal", D: "D - Dependent", B: "B - Both" }[formData.plan_type] : "Select" }}
                        onChange={(selected) => handleInputChange(selected.value, "plan_type")}
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pre-existing <span className="text-red-500">*</span></label>
                      <Select
                        options={[
                          { value: "", label: "Select" },
                          { value: "covered", label: "Covered" },
                          { value: "not covered", label: "Not Covered" },
                        ]}
                        value={{ value: formData.preexist, label: formData.preexist ? { covered: "Covered", "not covered": "Not Covered" }[formData.preexist] : "Select" }}
                        onChange={(selected) => handleInputChange(selected.value, "preexist")}
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                      />
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.ftw}
                          onChange={(e) => handleInputChange(e.target.checked, "ftw")}
                          className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Fit To Work
                        </span>
                        <span className="inline-block bg-yellow-100 text-blue-800 text-xs font-semibold mr-1 px-2.5 py-0.5 rounded">
                          Allow doctors to issue fit to work on ATAccess MedCert
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plan Limit Type</label>
                      <div className="mt-2 space-y-2">
                        {[
                          { value: "1", label: "MBL Only (Maximum Benefit Limit)" },
                          { value: "2", label: "ABL Only (Annual Benefit Limit)" },
                          { value: "3", label: "Both MBL & ABL" },
                        ].map((option) => (
                          <label key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              name="plan_limit_type"
                              value={option.value}
                              checked={formData.plan_limit_type === option.value}
                              onChange={(e) => handleInputChange(e.target.value, "plan_limit_type")}
                              className="h-4 w-4 text-blue-600 border-gray-200"
                            />
                            <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Intervening Period</label>
                      <input
                        type="number"
                        value={formData.intervening_prd || ""}
                        onChange={(e) => handleInputChange(e.target.value, "intervening_prd")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter number of days"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">CoPay c/o Company (%) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        value={formData.copay_corp || ""}
                        onChange={(e) => handleInputChange(e.target.value, "copay_corp")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter % value"
                        max="100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">CoPay c/o Member (%)</label>
                      <input
                        type="number"
                        value={formData.copay_member || ""}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                        placeholder="% value"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Dreaded Diseases Covered</label>
                      <textarea
                        value={formData.pec_dread || ""}
                        onChange={(e) => handleInputChange(e.target.value, "pec_dread")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={4}
                        placeholder="Enter dreaded diseases"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Other Exclusions/Modalities</label>
                      <input
                        type="text"
                        value={formData.exclusion1 || ""}
                        onChange={(e) => handleInputChange(e.target.value, "exclusion1")}
                        className="mt-1 block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter other exclusions/modalities"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Coverage</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("INPATIENT-ADMISSION")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_inpatient")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">INPATIENT</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("OUTPATIENT")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_outpatient")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">OUTPATIENT</span>
                        </label>
                        <div className="ml-6 space-y-2">
                          {[
                            { label: "Basic Consultation", code: "cov_basic_consultation" },
                            { label: "Referral to Specialist", code: "cov_referral_to_specialist" },
                            { label: "OP Laboratory", code: "cov_op_laboratory" },
                            { label: "OPDOR", code: "cov_opdor" },
                            { label: "Facility Fee", code: "cov_facility_fee" },
                            { label: "Clinic Setting", code: "cov_clinic_setting" },
                            { label: "ATAccess", code: "cov_ataccess" },
                            { label: "MedPadala", code: "cov_medpadala" },
                            { label: "MPsyche", code: "cov_mpsyche" },
                          ].map((item) => (
                            <label key={item.code} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.coverage_codes.includes(coverageMap[item.code.replace("cov_", "")][0])}
                                onChange={(e) => handleInputChange(e.target.checked, item.code)}
                                className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("APE")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_annual_physical_exam")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">ANNUAL PHYSICAL EXAM</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("EMERGENCY")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_emergency")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">EMERGENCY</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("MATERNITY")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_maternity")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">MATERNITY</span>
                        </label>
                        <div className="ml-6 space-y-2">
                          {[
                            { label: "Pre and Post Natal Consultation", code: "cov_pre_and_post_natal_consultation" },
                            { label: "Laboratory", code: "cov_laboratory" },
                            { label: "Delivery", code: "cov_delivery" },
                          ].map((item) => (
                            <label key={item.code} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.coverage_codes.includes(coverageMap[item.code.replace("cov_", "")][0])}
                                onChange={(e) => handleInputChange(e.target.checked, item.code)}
                                className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{item.label}</span>
                            </label>
                          ))}
                        </div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.coverage_codes.includes("REIMBURSEMENT")}
                            onChange={(e) => handleInputChange(e.target.checked, "cov_reimbursement")}
                            className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">REIMBURSEMENT</span>
                        </label>
                        <div className="ml-6 space-y-2">
                          {[
                            { label: "Inpatient", code: "cov_ri_inpatient" },
                            { label: "Outpatient", code: "cov_ri_outpatient" },
                          ].map((item) => (
                            <label key={item.code} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.coverage_codes.includes(coverageMap[item.code.replace("cov_", "")][0])}
                                onChange={(e) => handleInputChange(e.target.checked, item.code)}
                                className="h-4 w-4 text-blue-600 border-gray-200 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "benefit_limits" && (
                <div className="space-y-6">
                  <div className="-mx-6 border-y border-blue-100 bg-blue-50/60 px-6 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">Benefit Limits</h2>
                        <p className="text-sm text-gray-600">Set Basic and Major Benefit values per coverage.</p>
                      </div>
                      <TooltipProvider>
                        <Tooltip content="Show benefit limit rules and allowed values.">
                          <button
                            type="button"
                            onClick={() => setShowBenefitLimitSpecs((value) => !value)}
                            aria-expanded={showBenefitLimitSpecs}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                          >
                            <HelpCircle className="h-4 w-4" />
                            {showBenefitLimitSpecs ? "Hide Specs" : "View Specs"}
                          </button>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {showBenefitLimitSpecs && (
                      <div className="mt-4 grid gap-4 border-t border-gray-200 pt-4 text-sm text-gray-700 lg:grid-cols-3">
                        <div>
                          <p className="font-semibold text-gray-900">Allowed</p>
                          <ul className="mt-2 list-disc space-y-1 pl-5">
                            <li>Non-negative numbers for peso/count limits, e.g. 0, 1000, 1000.50.</li>
                            <li>Total Number of Visit accepts a non-negative number or unlimited.</li>
                            <li>Anesthesiologist Rate accepts a non-negative number; the benefit row may be saved with %.</li>
                          </ul>
                        </div>
                        <div className="lg:col-span-2">
                          <p className="font-semibold text-gray-900">Suggestion Options</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {suggestionOptions.map((option) => (
                              <span
                                key={`spec-${option}`}
                                className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800"
                              >
                                {option}
                              </span>
                            ))}
                          </div>
                          <p className="mt-3 text-xs text-gray-600">
                            Not allowed: negative values, free text outside these options, and % values outside Anesthesiologist Rate.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {[
                    {
                      title: "INPATIENT",
                      fields: [
                        { label: "Max Limit", name: "ben_lmt_ip_max_limit" },
                        { label: "Room & Board (Peso Based)", name: "ben_lmt_ip_room_n_board" },
                        {
                          label: "Room & Board (Category)",
                          name: "ben_lmt_ip_room_n_board_cat",
                          type: "select",
                          options: [
                            { value: "Ward", label: "Ward" },
                            { value: "Semi-Private", label: "Semi-Private" },
                            { value: "Small Private", label: "Small Private" },
                            { value: "Regular Private", label: "Regular Private" },
                            { value: "Large Private", label: "Large Private" },
                            { value: "Open Private", label: "Open Private" },
                            { value: "Suite", label: "Suite" },
                            { value: "ICU", label: "ICU" },
                          ],
                        },
                        { label: "Misc. Hospital Services", name: "ben_lmt_ip_misc_hospital_services" },
                        { label: "Surgical Fee", name: "ben_lmt_ip_surgical_fee" },
                        { label: "Physician Fee", name: "ben_lmt_ip_physician_fee" },
                        { label: "OPDOR", name: "ben_lmt_ip_opdor" },
                        { label: "ECU", name: "ben_lmt_ip_ecu" },
                        { label: "Anesthesiologist Rate", name: "ben_lmt_ip_surgical_fee_anes_fee" },
                        // { label: "Anesthesiologist Rate", name: "anes_basic_rate" },
                        // { label: "Anesthesiologist Rate", name: "anes_basic_rate", single: true },
                        // { label: "Anesthesiologist Fee", name: "ben_lmt_ip_surgical_fee_anes_fee" },
                      ],
                    },
                    {
                      title: "OUTPATIENT",
                      fields: [
                        { label: "Max Limit", name: "ben_lmt_op_max_limit" },
                        { label: "Total Number of Visit", name: "ben_lmt_op_total_no_visit_basic", single: true },
                        { label: "Basic Consultation", name: "ben_lmt_op_basic_consultation" },
                        { label: "Laboratory", name: "ben_lmt_op_laboratory" },
                        { label: "Clinic Setting", name: "ben_lmt_op_clinic_setting" },
                        { label: "Emergency", name: "ben_lmt_op_emergency" },
                        { label: "Medicine", name: "ben_lmt_op_medicine" },
                        { label: "ATAccess", name: "ben_lmt_op_ataccess" },
                        { label: "Teleconsult Count Limit", name: "ataccess_num", single: true },
                        { label: "MedPadala", name: "ben_lmt_op_medpadala" },
                        { label: "MPsyche", name: "ben_lmt_op_mpsyche" },
                        { label: "MPsyche Count Limit", name: "mpsyche_num", single: true },
                      ],
                    },
                    {
                      title: "MATERNITY",
                      fields: [
                        { label: "Max Limit", name: "ben_lmt_mat_max_limit" },
                        { label: "Normal/CS Delivery", name: "ben_lmt_mat_normal_delivery" },
                        // { label: "CS Delivery", name: "ben_lmt_mat_cs_delivery" },
                        { label: "Pre & Post Natal", name: "ben_lmt_mat_pre_natal" },
                        // { label: "Post-Natal", name: "ben_lmt_mat_post_natal" },
                      ],
                    },
                    {
                      title: "APE",
                      fields: [
                        { label: "Max Limit", name: "ben_lmt_ape_max_limit" },
                      ],
                    },
                  ].map((section) => (
                    <div key={section.title} className="border-b border-gray-200 pb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 underline">{section.title}</h3>
                      <div className="grid grid-cols-3 gap-4 items-center">
                        <div className="font-medium text-sm text-gray-700">Benefit</div>
                        <div className="font-medium text-sm text-gray-700">Basic Benefit</div>
                        <div className="font-medium text-sm text-gray-700">Major Benefit</div>
                        {section.fields.map((field) => (
                          <React.Fragment key={field.name}>
                            <div className="text-sm text-gray-700 self-center">{field.label}</div>
                            {field.type === "select" ? (
                              <>
                                <Select
                                  options={field.options}
                                  value={field.options.find((opt) => opt.value === formData[field.name])}
                                  onChange={(selected) => handleInputChange(selected.value, field.name)}
                                  className="text-sm"
                                  classNamePrefix="select"
                                />
                                <input
                                  type="text"
                                  value={formData[`${field.name}_basic`] || formData[`ben_lmt_ip_room_n_board_basic`] || ""}
                                  onChange={(e) => handleInputChange(e.target.value, `${field.name}_basic`)}
                                  onBlur={() => handleBlur(`${field.name}_basic`)}
                                  className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                                  placeholder="Enter basic limit"
                                  disabled
                                  list="benefitLimitSuggestions"
                                />
                                {/* <input
                                  type="text"
                                  value={formData[`${field.name}_major`] || formData[`ben_lmt_ip_room_n_board_major`] || ""}
                                  onChange={(e) => handleInputChange(e.target.value, `${field.name}_major`)}
                                  onBlur={() => handleBlur(`${field.name}_major`)}
                                  className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-100 cursor-not-allowed"
                                  placeholder="Enter major limit"
                                  disabled
                                  list="benefitLimitSuggestions"
                                /> */}
                              </>
                            ) : field.single ? (
                              <>
                                <input
                                  type="text"
                                  value={formData[field.name] || ""}
                                  onChange={(e) => handleInputChange(e.target.value, field.name)}
                                  onFocus={() => handleFocus(field.name)}
                                  onBlur={() => handleBlur(field.name)}
                                  className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder={`Enter ${field.label.toLowerCase()}`}
                                  list={getSuggestionListId(field.name)}
                                />
                                <div></div>
                              </>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  value={formData[`${field.name}_basic`] || ""}
                                  onChange={(e) => handleInputChange(e.target.value, `${field.name}_basic`)}
                                  onBlur={() => handleBlur(`${field.name}_basic`)}
                                  className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="Enter basic limit"
                                  list={getSuggestionListId(`${field.name}_basic`)}
                                />
                                <input
                                  type="text"
                                  value={formData[`${field.name}_major`] || ""}
                                  onChange={(e) => handleInputChange(e.target.value, `${field.name}_major`)}
                                  onBlur={() => handleBlur(`${field.name}_major`)}
                                  className="block w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  placeholder="Enter major limit"
                                  list={getSuggestionListId(`${field.name}_major`)}
                                />
                              </>
                            )}
                            <datalist id="benefitLimitSuggestions">
                              {suggestionOptions.map((option) => (
                                <option key={option} value={option} />
                              ))}
                            </datalist>
                            <datalist id="totalVisitSuggestions">
                              {totalVisitSuggestionOptions.map((option) => (
                                <option key={option} value={option} />
                              ))}
                            </datalist>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="sticky bottom-0 z-30 -mx-6 mt-8 flex flex-col gap-3 border-t border-gray-200 bg-white/95 px-6 py-4 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => navigate("/plan-and-coverage-benefits-setup")}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/plan-and-coverage-benefits-setup")}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isLoading}
                >
                  {isLoading && <ArrowPathIcon className="h-5 w-5 animate-spin" />}
                  Save Plan Details
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AddPlanForm;
